import type {ScanMeasurement} from "utils/funnel";
import { makeAutoObservable } from "mobx"
import { translate } from "i18n/translator"
// Types only — importing the request layer eagerly would create a module-load
// cycle (requests → utils → rootStore → grammarScanStore). waitTaskResult is
// pulled in lazily inside waitGrammarResult instead.
import type { GrammarMatch, GrammarScanData, GrammarSeverity } from "requests"
import type { RootStore } from "stores/rootStore"
import { EditorStore } from "stores/editorStore"

export interface GrammarGroup {
  id: string
  label: string
  severity: GrammarSeverity
  matches: GrammarMatch[]
}

/** Span the in-text highlight covers: just the flagged word, or its whole sentence. */
export type FocusScope = "word" | "sentence"

/** Same test RequestStatusStore uses; inlined to keep this store free of root wiring. */
const isCanceledError = (error: any): boolean =>
  !!(
    (error && (error.name === "CanceledError" || error.code === "ERR_CANCELED" || error.message === "canceled")) ||
    (typeof DOMException !== "undefined" && error instanceof DOMException && error.name === "AbortError")
  )

/**
 * Design decision (2026-07): suggestions come in exactly TWO buckets — red
 * "Grammar" (error-severity matches) and blue "Readability" (style matches).
 * Yellow warnings and the Possible Typo / Typography LanguageTool categories
 * are dropped on load entirely; the per-LT-category grouping is gone.
 */
export const GRAMMAR_GROUP_ID = "grammar"
export const READABILITY_GROUP_ID = "readability"

// LanguageTool category ids removed from the results wholesale (design).
const DROPPED_CATEGORY_IDS = new Set(["TYPOS", "TYPOGRAPHY"])

export function grammarGroupId(m: GrammarMatch): string {
  return m.severity === "error" ? GRAMMAR_GROUP_ID : READABILITY_GROUP_ID
}

/**
 * The matches the panel surfaces. Design: yellow warnings (whitespace/duplication/
 * inconsistency), the Possible Typo / Typography categories and matches without a
 * suggested replacement (nothing to Accept — the whole UX is built around applying a
 * fix) are dropped — only actionable red Grammar and blue Readability suggestions stay.
 * Anything that counts the issues (the batch plate's chip) counts these, so the number
 * and the panel never disagree.
 */
export function actionableGrammarMatches(data: GrammarScanData): GrammarMatch[] {
  return data.matches.filter(
    m => m.severity !== "warning"
      && !DROPPED_CATEGORY_IDS.has(m.category_id)
      && m.replacements.length > 0
  )
}

/**
 * Quote rules (QUOTATION_MARKS_COMMA, EN_UNPAIRED_QUOTES) flag one or two characters
 * — a lone `",` or `"`. Highlighting just those reads as "the app underlined a random
 * comma": the fix is about the sentence's reported-speech punctuation, not that
 * character. Such matches always focus their whole sentence. PRO-1752
 */
export function isQuoteRule(m: GrammarMatch): boolean {
  return m.rule_id.includes("QUOT")
}

export function grammarGroupLabel(m: GrammarMatch): string {
  return m.severity === "error"
    ? translate("grammar-group-grammar", undefined, "Grammar")
    : translate("grammar-group-readability", undefined, "Readability")
}

/**
 * Full snapshot of the reversible grammar state. Accept/dismiss actions mutate
 * `workingText`, `matches` (with offset shifts) and `dismissedIds`; storing a
 * snapshot before each user action makes undo/redo exact without having to
 * reverse offset arithmetic by hand. An Accept-all is one snapshot → one undo.
 */
interface GrammarSnapshot {
  workingText: string
  matches: GrammarMatch[]
  dismissedIds: string[]
  // Caret offset to restore in the editor after undo/redo (text edits only).
  caret?: number
}

// Consecutive keystrokes within this window collapse into a single undo step so
// undo rolls back a burst of typing, not one character at a time.
const TEXT_COALESCE_MS = 600

export class GrammarScanStore {
  isLoading = false
  language = ""
  score: number | null = null
  originalText = ""
  workingText = ""
  matches: GrammarMatch[] = []
  dismissedIds = new Set<string>()
  hoveredId: string | null = null
  expandedCardId: string | null = null
  // The currently focused suggestion — drives the in-text phrase highlight, the
  // left-gutter marker focus and the scroll target. One source of truth shared by
  // the right column and the editor.
  activeMatchId: string | null = null
  // Whether the in-text highlight spans just the flagged word or the whole
  // sentence. Word interactions (hovering/clicking an error in the text) light up
  // only the word; sentence interactions (a gutter marker, an opened card) light
  // up the sentence. Tracked separately for hover vs active focus so they don't
  // bleed into each other.
  hoverScope: FocusScope = "sentence"
  activeScope: FocusScope = "sentence"
  // Accordion is single-open: at most one category section expanded at a time.
  // `undefined` = untouched (first group open by default), `null` = user collapsed
  // everything, string = a specific category id is open.
  openCategoryId: string | null | undefined = undefined
  // Mobile: tapping an underlined phrase opens a compact swipeable point-fix
  // card carousel at the bottom (the mobile analog of the desktop popover).
  // Holds the match the carousel is currently showing; null = closed.
  pointFixMatchId: string | null = null

  // Undo/redo of the working text — both direct typing/paste/delete AND the
  // grammar actions (accept / dismiss / accept-all), since they all change the
  // text. Snapshots of the state *before* each change; restoring rolls the text
  // back, re-underlines phrases and (for text edits) restores the caret.
  private undoStack: GrammarSnapshot[] = []
  private redoStack: GrammarSnapshot[] = []
  // Bumped on every undo/redo restore so the editor re-syncs its DOM + caret.
  textRestoreNonce = 0
  // Caret offset to apply on the next restore (null = leave caret untouched,
  // e.g. undoing an accept/dismiss rather than a text edit).
  restoreCaret: number | null = null
  // Coalescing bookkeeping for burst typing.
  private lastTextEditAt = 0
  private lastWasTextEdit = false

  /** The tab's text while it has no checked text to show — the run in flight, or one wiped. */
  editor: EditorStore

  constructor(root: RootStore) {
    this.editor = new EditorStore(root)
    makeAutoObservable(this)
  }

  setLoading(value: boolean) {
    this.isLoading = value
  }

  /** Open the point-fix carousel at a tapped match (mobile). */
  openPointFix(matchId: string) {
    const m = this.matches.find(x => x.id === matchId)
    if (!m || this.dismissedIds.has(matchId)) return
    this.pointFixMatchId = matchId
    this.activeMatchId = matchId
    this.activeScope = "word"
    this.scrollBlock = "nearest" // the tapped word is already under the finger
    this.scrollNonce++
  }

  /** Move the carousel to another match (swipe / after accept-dismiss). */
  setPointFixMatch(matchId: string) {
    this.pointFixMatchId = matchId
    this.activeMatchId = matchId
    this.activeScope = "word"
    // Swiping lands on a match that may be anywhere in the document — scroll
    // it to the TOP of the text view (the carousel covers the bottom).
    this.scrollBlock = "start"
    this.scrollNonce++
  }

  closePointFix() {
    this.pointFixMatchId = null
    this.activeMatchId = null
  }

  setHovered(id: string | null, scope: FocusScope = "sentence") {
    this.hoveredId = id
    if (id) this.hoverScope = this.highlightScope(id, scope)
  }

  /** Widen a word-scoped highlight to the sentence when the rule needs it (see isQuoteRule). */
  private highlightScope(matchId: string, scope: FocusScope): FocusScope {
    if (scope === "sentence") return scope
    const m = this.matches.find(x => x.id === matchId)
    return m && isQuoteRule(m) ? "sentence" : scope
  }

  /** Expand the clicked sentence card, or collapse it if it was already open. */
  toggleCard(id: string) {
    this.expandedCardId = this.expandedCardId === id ? null : id
  }

  /** Open a specific card (or close all when null). Used to advance to the next suggestion. */
  setExpandedCard(id: string | null) {
    this.expandedCardId = id
  }

  /**
   * The user typed/edited directly in the grammar view. The previous matches are
   * now stale (offsets no longer line up), so drop all suggestions and overlays
   * and keep only the edited text — the view stays mounted as a plain editor and
   * a re-scan rebuilds the suggestions.
   */
  applyManualEdit(text: string, caret?: number) {
    // Record the pre-edit state so this typing can be undone (coalescing bursts).
    // Unlike before, we do NOT clear history — undo/redo now covers text edits.
    this.pushTextSnapshot(caret)
    this.workingText = text
    this.matches = []
    this.dismissedIds.clear()
    this.hoveredId = null
    this.activeMatchId = null
    this.expandedCardId = null
    this.pointFixMatchId = null
  }

  /** Snapshot the current state before a text edit, collapsing burst typing. */
  private pushTextSnapshot(caret?: number) {
    const now = Date.now()
    const coalesce = this.lastWasTextEdit && now - this.lastTextEditAt < TEXT_COALESCE_MS && this.undoStack.length > 0
    this.lastTextEditAt = now
    this.lastWasTextEdit = true
    if (coalesce) return
    this.undoStack.push({ ...this.takeSnapshot(), caret })
    this.redoStack = []
  }

  // Bumped on every activation so the text view re-runs its scroll effect even
  // when the same match is activated twice (re-clicking an open card).
  scrollNonce = 0
  // How the text view scrolls to the activated match: "start" pins it to the
  // top of the view (list clicks, popover advance, carousel swipes), "nearest"
  // only nudges it into view (tapping the word itself — the text must not
  // jump under the finger).
  scrollBlock: "start" | "nearest" = "nearest"

  /** Set the focused suggestion (phrase highlight + gutter marker + scroll). */
  setActiveMatch(id: string | null, scope: FocusScope = "sentence") {
    this.activeMatchId = id
    if (id) {
      this.activeScope = this.highlightScope(id, scope)
      this.scrollBlock = scope === "sentence" ? "start" : "nearest"
      this.scrollNonce++
    }
  }

  // Start of the sentence containing [offset, offset+length) — mirrors
  // sentenceBounds() in segments.tsx (kept local to avoid a store→segments→
  // requests import cycle). Used to resolve a match to its sidebar card id.
  private sentenceStart(offset: number): number {
    const BREAK = new Set([".", "!", "?", "\n"])
    for (let i = offset - 1; i >= 0; i--) {
      if (BREAK.has(this.workingText[i])) return i + 1
    }
    return 0
  }

  /**
   * Open the suggestion for a match in the right column (section + card) and
   * focus it. `scope` controls the in-text highlight: markers/cards focus the
   * sentence (and scroll it to the top), clicking the word itself keeps the
   * word focus so the text doesn't jump under the cursor.
   */
  openSuggestionByMatch(matchId: string, scope: FocusScope = "sentence") {
    const m = this.matches.find(x => x.id === matchId)
    if (!m || this.dismissedIds.has(matchId)) return
    const groupId = grammarGroupId(m)
    this.openCategoryId = groupId
    this.expandedCardId = `${groupId}:${this.sentenceStart(m.offset)}`
    this.activeMatchId = matchId
    this.activeScope = this.highlightScope(matchId, scope)
    // Keyed on the REQUESTED scope, not the widened one: a click on the word still
    // must not scroll the text out from under the cursor.
    this.scrollBlock = scope === "sentence" ? "start" : "nearest"
    this.scrollNonce++
  }

  /** The category that is effectively open: explicit choice, or the first group by default. */
  get effectiveOpenCategoryId(): string | null {
    if (this.openCategoryId === undefined) return this.groups[0]?.id ?? null
    return this.openCategoryId
  }

  isCategoryOpen(id: string): boolean {
    return this.effectiveOpenCategoryId === id
  }

  /** Single-open accordion: opening a section closes the other; clicking the open one collapses all. */
  toggleCategory(id: string) {
    this.openCategoryId = this.isCategoryOpen(id) ? null : id
  }

  /** Force a specific category open (or collapse all with null). Used to advance to the next section after Accept all. */
  setOpenCategory(id: string | null) {
    this.openCategoryId = id
  }

  load(data: GrammarScanData) {
    // A partial run checked the text up to its cut; the rest stays in the tab, unchecked.
    // Matches index into the checked part, so appending keeps their offsets.
    const cut = this.editor.cut
    const text = cut ? data.text + cut.text.slice(cut.at) : data.text
    this.originalText = text
    this.workingText = text
    this.matches = actionableGrammarMatches(data)
    this.language = data.language
    this.score = data.score ?? null
    this.dismissedIds.clear()
    this.pointFixMatchId = null
    this.clearHistory()
    this.isLoading = false
  }

  clear() {
    this.originalText = ""
    this.workingText = ""
    this.matches = []
    this.language = ""
    this.score = null
    this.dismissedIds.clear()
    this.hoveredId = null
    this.expandedCardId = null
    this.activeMatchId = null
    this.openCategoryId = undefined
    this.pointFixMatchId = null
    this.clearHistory()
    this.isLoading = false
    this.editor.clear()
  }

  reset() {
    this.workingText = this.originalText
    this.dismissedIds.clear()
    this.clearHistory()
  }

  get visibleMatches(): GrammarMatch[] {
    return this.matches.filter(m => !this.dismissedIds.has(m.id))
  }

  /** Visible matches in document order — the point-fix carousel swipes through these. */
  get orderedMatches(): GrammarMatch[] {
    return [...this.visibleMatches].sort((a, b) => a.offset - b.offset)
  }

  get stats(): { errors: number; warnings: number; style: number; total: number } {
    const stats = { errors: 0, warnings: 0, style: 0, total: 0 }
    for (const m of this.visibleMatches) {
      stats.total++
      if (m.severity === "error") stats.errors++
      else if (m.severity === "warning") stats.warnings++
      else stats.style++
    }
    return stats
  }

  /**
   * Exactly two fixed buckets (design): red "Grammar" — every error-severity
   * match, typos included — then blue "Readability" with the style matches.
   * Matches inside a group keep document order; empty groups are omitted.
   */
  get groups(): GrammarGroup[] {
    const byOffset = (a: GrammarMatch, b: GrammarMatch) => a.offset - b.offset
    const errors = this.visibleMatches.filter(m => m.severity === "error").sort(byOffset)
    const style = this.visibleMatches.filter(m => m.severity !== "error").sort(byOffset)
    const groups: GrammarGroup[] = []
    if (errors.length) {
      groups.push({
        id: GRAMMAR_GROUP_ID,
        label: translate("grammar-group-grammar", undefined, "Grammar"),
        severity: "error",
        matches: errors,
      })
    }
    if (style.length) {
      groups.push({
        id: READABILITY_GROUP_ID,
        label: translate("grammar-group-readability", undefined, "Readability"),
        severity: "style",
        matches: style,
      })
    }
    return groups
  }

  // ---- Undo / redo of grammar actions -------------------------------------

  private takeSnapshot(): GrammarSnapshot {
    return {
      workingText: this.workingText,
      matches: this.matches.map(m => ({ ...m })),
      dismissedIds: Array.from(this.dismissedIds),
    }
  }

  private restoreSnapshot(s: GrammarSnapshot) {
    this.workingText = s.workingText
    this.matches = s.matches.map(m => ({ ...m }))
    this.dismissedIds = new Set(s.dismissedIds)
    // Tell the editor to re-render its DOM from workingText and place the caret.
    this.restoreCaret = s.caret ?? null
    this.textRestoreNonce++
    // The next keystroke must start a fresh undo step, not merge into this one.
    this.lastWasTextEdit = false
  }

  /** Record the pre-action state. Call once per user action (before mutating). */
  private beginAction() {
    this.undoStack.push(this.takeSnapshot())
    this.redoStack = []
    // A grammar action breaks the typing burst so the next edit is its own step.
    this.lastWasTextEdit = false
  }

  private clearHistory() {
    this.undoStack = []
    this.redoStack = []
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0
  }

  /** Revert the last grammar action: text rolls back, suggestion returns to its section. */
  undo() {
    const prev = this.undoStack.pop()
    if (!prev) return
    this.redoStack.push(this.takeSnapshot())
    this.restoreSnapshot(prev)
  }

  /** Re-apply the last undone action. */
  redo() {
    const next = this.redoStack.pop()
    if (!next) return
    this.undoStack.push(this.takeSnapshot())
    this.restoreSnapshot(next)
  }

  // ---- Accept / dismiss ----------------------------------------------------

  /** Apply the first suggested replacement for every match in a category. */
  acceptCategory(categoryId: string) {
    const group = this.groups.find(g => g.id === categoryId)
    if (!group) return
    // Capture (id, replacement) up front: _applyMatch mutates the matches array
    // and shifts offsets, but ids stay stable so sequential application is safe.
    const toApply = group.matches
      .filter(m => m.replacements[0])
      .map(m => ({ id: m.id, replacement: m.replacements[0] }))
    if (!toApply.length) return
    this.beginAction()
    for (const { id, replacement } of toApply) {
      this._applyMatch(id, replacement)
    }
  }

  /** Apply the first suggested replacement for every match in the given list (e.g. one sentence card). */
  acceptMatches(ids: string[]) {
    // Capture (id, replacement) up front: _applyMatch shifts offsets, but ids stay stable.
    const toApply = ids
      .map(id => this.matches.find(m => m.id === id))
      .filter((m): m is GrammarMatch => !!m && !!m.replacements[0])
      .map(m => ({ id: m.id, replacement: m.replacements[0] }))
    if (!toApply.length) return
    this.beginAction()
    for (const { id, replacement } of toApply) {
      this._applyMatch(id, replacement)
    }
  }

  /** Apply the first suggested replacement for every visible match. */
  acceptAll() {
    const toApply = this.visibleMatches
      .filter(m => m.replacements[0])
      .map(m => ({ id: m.id, replacement: m.replacements[0] }))
    if (!toApply.length) return
    this.beginAction()
    for (const { id, replacement } of toApply) {
      this._applyMatch(id, replacement)
    }
  }

  /** Single accept (one suggestion) — its own undo step. */
  applyMatch(id: string, replacement: string) {
    if (!this.matches.some(m => m.id === id)) return
    this.beginAction()
    this._applyMatch(id, replacement)
  }

  /** Raw replacement application without touching history (shared by accept paths). */
  private _applyMatch(id: string, replacement: string) {
    const idx = this.matches.findIndex(m => m.id === id)
    if (idx === -1) return
    const m = this.matches[idx]
    this.workingText =
      this.workingText.slice(0, m.offset) +
      replacement +
      this.workingText.slice(m.offset + m.length)

    const delta = replacement.length - m.length
    const remaining: GrammarMatch[] = []
    for (let i = 0; i < this.matches.length; i++) {
      if (i === idx) continue
      const other = this.matches[i]
      if (other.offset >= m.offset + m.length) {
        remaining.push({ ...other, offset: other.offset + delta })
      } else {
        remaining.push(other)
      }
    }
    this.matches = remaining
  }

  dismiss(id: string) {
    if (this.dismissedIds.has(id)) return
    this.beginAction()
    this.dismissedIds.add(id)
  }

  /** Dismiss several matches as one undo step (e.g. dismissing a whole card). */
  dismissMatches(ids: string[]) {
    const fresh = ids.filter(id => !this.dismissedIds.has(id))
    if (!fresh.length) return
    this.beginAction()
    fresh.forEach(id => this.dismissedIds.add(id))
  }

  async waitGrammarResult(task_id: number, measurement: ScanMeasurement = {}) {
    const startedAt = performance.now()
    this.setLoading(true)
    try {
      const { waitTaskResult } = await import("requests")
      const response: any = await waitTaskResult(task_id)
      const data = response.data as GrammarScanData
      this.load(data)
      void Promise.all([import("analytics"), import("utils/gtm")]).then(([{logEvent}, {gtmAppScanSuccess}]) =>
        logEvent(gtmAppScanSuccess({...measurement, task_id, elapsed_ms: Math.round(performance.now() - startedAt), scan_type: 'grammar', scan_result: String(data.stats?.total ?? ''), page_type: 'app_scan', entrypoint: 'grammar_result'}))
      ).catch(() => {}) // Analytics must not turn a completed scan into an error.
    } catch (e: any) {
      this.setLoading(false)
      console.warn("[Grammar error]", e)
      // An aborted poll means another scan took over — not something to report.
      if (isCanceledError(e)) return
      // Mark the source and rethrow: the scan-level handler in inputStore names
      // the failed check in the banner, and the original error travels on so its
      // backend code still drives the limit paywall. Swallowing it here is what
      // left a failed grammar scan looking like an empty result.
      if (e && typeof e === "object") e.scanKind = "grammar"
      throw e
    }
  }
}
